import type { HarnessProgress } from '~/components/pages/domain/components/domainCard';
import { toolboxApi, unwrapResult, type ApiEnvelope } from '~/modules/query/base';

/** GET /harnesses.onboarding/NEW — the harness skeleton the progress UI renders. */
export type OnboardingSkeleton = HarnessProgress;

/** GET /harnesses.onboarding/{moniker}/progress */
export type OnboardingProgress = HarnessProgress;

/** POST /harnesses.onboarding body (the onboarding form values). */
export type OnboardingPayload = Record<string, unknown>;

export interface OnboardingCreateArgs {
  payload: OnboardingPayload;
  raze?: boolean;
}

/** POST /harnesses.onboarding — callers read `status`. */
export type OnboardingCreateResponse = ApiEnvelope<unknown>;

const onboardingApi = toolboxApi.injectEndpoints({
  endpoints: (build) => ({
    getSkeleton: build.query<OnboardingSkeleton, void>({
      query: () => ({ url: '/harnesses.onboarding/NEW', method: 'GET' }),
      transformResponse: unwrapResult<OnboardingSkeleton>,
      providesTags: ['Onboarding'],
    }),

    // The envelope is kept because the page reads `status` to tell a started run from a refused one.
    createOnboarding: build.mutation<OnboardingCreateResponse, OnboardingCreateArgs>({
      query: ({ payload, raze = false }) => ({
        url: `/harnesses.onboarding?runAsync=t${raze ? '&raze=t' : ''}`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['Onboarding'],
    }),

    getOnboardingProgress: build.query<OnboardingProgress, string>({
      query: (moniker) => ({
        url: `/harnesses.onboarding/${encodeURIComponent(moniker)}/progress`,
        method: 'GET',
      }),
      transformResponse: unwrapResult<OnboardingProgress>,
      providesTags: ['Onboarding'],
    }),
  }),
});

export const {
  useLazyGetSkeletonQuery,
  useCreateOnboardingMutation,
  useGetOnboardingProgressQuery,
} = onboardingApi;
